/** Approved starting copy. Frontend should import this content surface instead of scattering strings. */
export const copy = {
  brand: {
    product: "Type Set",
    byline: "A presentation tool by pitch.dog",
    shortPromise: "Find the voice. Make it earn the room.",
  },
  home: {
    eyebrow: "A SMALL PITCH.DOG TOOL FOR BETTER DECKS",
    headline: "A deck should sound like somebody.",
    standfirst:
      "Answer a short, useful brief. Meet five distinct directions in 64 exact open-source families. Then make them survive a real slide.",
    trust: ["Choose how deep to go", "Meet five real directions", "Test the actual letterforms"],
    primaryAction: "Find my type direction",
    primaryHint: "Choose the useful 90-second answer or consent to the properly nosy four-minute ride.",
    typeAction: "Find my type direction",
    typeHint: "For a deck that needs a clearer voice and stronger reading hierarchy.",
    collectionAction: "Browse the font sources",
    methodAction: "See how it works",
    boundary:
      "You get a practical starting point, not design destiny. Change it, argue with it, make it yours.",
  },
  route: {
    type: {
      title: "Find the right voice",
      intro: "Choose a useful short answer or take the longer, sharper ride.",
    },
  },
  questions: {
    artifact: {
      title: "What are you making?",
      note: "Different decks ask different things of type. Pick the closest fit.",
    },
    mandatoryFont: {
      title: "Do you have to use a brand font?",
      note: "If the answer is yes, we respect it and check around it.",
    },
    toolAndHandoff: {
      title: "Where will you build and share it?",
      note: "A live presentation, a PDF, and an editable handoff need different kinds of reliability.",
    },
    viewing: {
      title: "Where will people see it?",
      note: "Choose every place that matters: room, laptop, phone, print, or a mix.",
    },
    pressure: {
      title: "How full are your typical slides?",
      note: "Be honest. We are designing for the deck you have, not the fantasy edit.",
    },
    content: {
      title: "What does the deck need to carry?",
      note: "Choose the real jobs: images, paragraphs, numbers, tables, or a bit of everything.",
    },
    writingSystems: {
      title: "Which languages must this deck handle?",
      note: "English and other Latin-script languages are selected. Add another only if the deck genuinely needs it.",
    },
    character: {
      title: "How much personality can the titles have?",
      note: "Quiet, confident, or expressive. Body text still has to do an honest day’s work.",
    },
    realWords: {
      title: "Try it with one real slide.",
      note: "A title and a short body are enough. Extra fields are there if you need them.",
      useSample: "Use a realistic sample",
      clear: "Clear my words",
    },
  },
  processing: {
    type: ["Checking the practical constraints", "Removing weak fits", "Comparing the strongest directions", "Preparing your result"],
    noPercent: "This should only take a moment.",
  },
  result: {
    eyebrow: "YOUR STARTING POINT",
    candidateBanner:
      "Every letterform here comes from the exact local font file. Deck-app installation and export still need one honest test.",
    one: "One system survived everything you asked of it. That is useful information.",
    two: "Two systems survived. Here is where we would begin—and the honest alternative.",
    three: "Three systems survived. Start here. Keep the others close enough to argue back.",
    relationshipLabel: "How they work together",
    proofLabel: "See it on real slides",
    whyLabel: "Why this fits",
    breakLabel: "Watch out for",
    provenanceLabel: "What came from where",
    evidenceLabel: "Technical details and sources",
    revise: "Change my answers",
    compare: "Other directions worth seeing",
    takeAway: "Download my direction",
  },
  specimens: {
    cover: "The opening",
    titleBody: "The explanation",
    quote: "The voice",
    imageText: "The image plus argument",
    evidence: "The evidence",
    overflow: "This is not a smaller-font problem. This page is carrying two pages’ work.",
    overflowActions: ["Shorten the copy", "Split the page", "Choose a roomier layout", "Compare a denser proven system"],
    fitTruth: "Character counts warn. Exact fonts, geometry, breakpoint, lines, and occupied bounds decide.",
  },
  collection: {
    headline: "Sixty-four real families. Wearing their own clothes.",
    body:
      "Every specimen uses its exact local font file, with its maker, OFL license, and official source attached. Browse slowly. Type deserves better than a beauty pageant in six identical sans-serifs.",
    tryWords: "Test this direction",
    people: "Meet the people behind the type",
    askFit: "Check it against my deck",
    remembered:
      "You came in with this one. We will keep it for comparison, but it still has to survive your actual handoff.",
  },
  boundaries: {
    mandatoryUnknown: {
      title: "Brand rule first.",
      body:
        "We do not have enough proof to evaluate this mandatory family. A name is not an editable file, a license, a fallback, or an application test.",
      action: "Show me the audit checklist",
    },
    scriptUnsupported: {
      title: "We would rather stop here than lose somebody’s name later.",
      body: "No system in this edition has earned support for every writing system you selected.",
    },
    zeroType: {
      title: "Nothing survived without bluffing.",
      body: "Here is the first material blocker, what we checked, and the smallest useful next move.",
    },
    fontLoadFailure: {
      title: "That typeface did not arrive.",
      body: "You are seeing a fallback, so comparison is paused. We will never put the target font’s name on substitute outlines.",
    },
  },
  export: {
    title: "Take your direction with you",
    slides: "PowerPoint / Keynote / Google Slides",
    canva: "Canva",
    designWeb: "Figma / Framer / web",
    collaborator: "A collaborator",
    other: "Somewhere stranger",
    actions: {
      text: "Download my direction",
      guide: "Download the one-page usage guide",
      css: "Copy CSS variables",
      json: "Download design tokens",
      manifest: "Download sources and evidence",
      project: "Save this project locally",
      print: "Print the specimen",
    },
    projectDisclosure:
      "Project file includes your type answers, chosen direction, sources, caveats, and any preview copy you chose to keep. It contains no account data and never phones home.",
  },
  project: {
    saved: "Saved in this browser.",
    reopen: "Open a Type Set project",
    relink: "No source file needed",
    exactMatch: "Same direction. Exact local faces where available. Carry on.",
    mismatch: "This project has no finished type direction.",
    mismatchAction: "Start a new fitting",
    clear: "Forget this project on this device",
  },
  promotion: {
    eyebrow: "WHEN A STARTING POINT IS NOT ENOUGH",
    headline: "A tool can set the look. It cannot make the pitch.",
    body:
      "If the story, structure, evidence, or deck itself needs a human team, pitch.dog does that work. No trapdoor. No hostage email. No sudden sales rep rappelling from the ceiling. You already have the complete direction this tool can honestly give.",
    action: "See how pitch.dog can help",
  },
  footer: {
    line: "Made with care, useful doubt, and a healthy dislike of design theatre by pitch.dog.",
    links: ["How it works", "Font sources", "Open saved project", "pitch.dog"],
  },
} as const;

export type TypeSetCopy = typeof copy;
